import type { FC, ReactElement } from "react"

import isEmpty from "lodash-es/isEmpty"
import isNull from "lodash-es/isNull"
import isString from "lodash-es/isString"
import { Controller } from "react-hook-form"

import {
    Divider,
    EDividerColor,
    ETextAreaBorderWidth,
    ETextAreaVariant,
    ETypographyAlignment,
    ETypographyColor,
    ETypographySize,
    ETypographyTag,
    ETypographyWeight,
    ErrorHint,
    TextArea,
    Typography
} from "@/3514/components"
import { extractStringFromOneLevelNestedHTML, getCssIdentifierFromFileName, renderRichContent } from "@/3514/utils"
import { type IUseTranslation, useTranslation } from "@/hooks"
import { richTextRendererUtils } from "@/shared/rich-text-renderer"

import { useFreeResponsesContext as useContext } from "./free-responses.context"
import { freeResponsesStyles as classes } from "./free-responses.styles"
import { EFreeResponsesFormField as EFormField, type TFreeResponsesQuestion as TQuestion } from "./free-responses.types"

type TProps = { index: number }

const displayName: string = "FreeResponse"
const editorClassNameIdentifier: string = getCssIdentifierFromFileName(displayName, "edi")

const FreeResponse: FC<TProps> = ({ index }: TProps): ReactElement => {
    const { t }: IUseTranslation = useTranslation()

    const {
        onQuestionComplete,
        questionList: questions,
        isError,
        form: {
            isSubmitting,
            methods: { control },
            errors
        }
    }: ReturnType<typeof useContext> = useContext()

    const data: TQuestion = questions[index]

    const fieldError: string = isEmpty(errors[EFormField.QuestionList]?.[index]?.answer?.message)
        ? String("")
        : errors[EFormField.QuestionList][index].answer.message

    const wasSubmitted: boolean = !isNull(data.submittedState)

    return (
        <div className={classes.field()}>
            <div className={classes.fieldWrapper()}>
                {richTextRendererUtils.isStringPlainText(data.initialState.question) ? (
                    <Typography
                        text={data.initialState.question}
                        color={ETypographyColor.Black}
                        size={ETypographySize.Small}
                        weight={ETypographyWeight.Bold}
                        alignment={ETypographyAlignment.Left}
                        tag={ETypographyTag.Paragraph}
                        className={classes.fieldTitle()}
                    />
                ) : (
                    renderRichContent({
                        identifier: editorClassNameIdentifier,
                        editorClasses: classes.fieldTitle(),
                        classes: classes.fieldTitleContent(editorClassNameIdentifier)
                    })(data.initialState.question)
                )}

                <Divider width="calc(100% - 16px)" height={1} variant={EDividerColor.MediumBlue} />

                <Controller
                    name={`${EFormField.QuestionList}.${index}.answer`}
                    control={control}
                    render={({ field }): ReactElement => {
                        async function handleBlur(): Promise<void> {
                            field.onBlur()

                            await onQuestionComplete({
                                answer: field.value,
                                id: data.initialState.id,
                                question: data.initialState.question
                            })
                        }

                        return (
                            <TextArea
                                key={data.initialState.id}
                                {...field}
                                variant={ETextAreaVariant.Transparent}
                                placeholderTextSize={ETypographySize.Medium}
                                placeholder={
                                    isString(data.initialState.placeholder)
                                        ? richTextRendererUtils.isStringPlainText(data.initialState.placeholder)
                                            ? data.initialState.placeholder
                                            : extractStringFromOneLevelNestedHTML(data.initialState.placeholder)
                                        : t("common.inputPlaceholder")
                                }
                                isDisabled={wasSubmitted || isSubmitting || isError}
                                textColor={ETypographyColor.Dark}
                                textSize={ETypographySize.Small}
                                borderWidth={ETextAreaBorderWidth.Tiny}
                                borderRadius={{ tl: 10, tr: 10, bl: 10, br: 10 }}
                                withItalicPlaceholder
                                withNoResize
                                withSpellingCssOverride
                                onBlur={handleBlur}
                                enterKeyHint="done"
                                className={classes.fieldInput()}
                                {...(wasSubmitted && { withDynamicHeight: true })}
                                {...(wasSubmitted ? {} : { minLines: 4 })}
                            />
                        )
                    }}
                />
            </div>

            <ErrorHint isVisible={!!fieldError} text={fieldError} position="center" variant="error" />
        </div>
    )
}

FreeResponse.displayName = displayName

export { FreeResponse }
